/** Interval for `registration.update()` while the PWA shell is open. */
export const PWA_UPDATE_CHECK_MS = 30 * 60 * 1000;

export const PWA_SKIP_WAITING_MESSAGE = 'SKIP_WAITING';

export function waitingServiceWorkerFingerprint(
  registration: ServiceWorkerRegistration | null,
): string {
  const waiting = registration?.waiting;
  if (!waiting) {
    return '';
  }
  return `${waiting.scriptURL}|${waiting.state}`;
}

export function shouldPromptForPwaUpdate(registration: ServiceWorkerRegistration | null): boolean {
  if (!navigator.serviceWorker.controller) {
    return false;
  }
  return Boolean(registration?.waiting);
}

/**
 * Activates a waiting worker (when present) and reloads so new bundles load.
 */
export async function applyPwaServiceWorkerUpdate(
  registration: ServiceWorkerRegistration,
  options?: { skipWaitingMessage?: string },
): Promise<void> {
  const skipWaitingMessage = options?.skipWaitingMessage ?? PWA_SKIP_WAITING_MESSAGE;
  let hasReloaded = false;

  const reloadPage = () => {
    if (hasReloaded) {
      return;
    }
    hasReloaded = true;
    window.location.reload();
  };

  const waitingWorker = registration.waiting;
  if (waitingWorker) {
    const onControllerChange = () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      reloadPage();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    waitingWorker.postMessage({ type: skipWaitingMessage });
    setTimeout(reloadPage, 1000);
    return;
  }

  if (registration.active) {
    reloadPage();
    return;
  }

  await registration.update();
  reloadPage();
}

export type PwaUpdateSignal = 'waiting' | 'controller';

export type AttachPwaRegistrationUpdateChecksOptions = {
  checkIntervalMs?: number;
};

/**
 * Polls for SW updates and notifies when a new worker is waiting or the controller changes
 * after an existing controller was already active (auto-activated SW without page reload).
 */
export function attachPwaRegistrationUpdateChecks(
  registration: ServiceWorkerRegistration,
  onUpdateAvailable: (signal: PwaUpdateSignal) => void,
  options?: AttachPwaRegistrationUpdateChecksOptions,
): () => void {
  const intervalMs = options?.checkIntervalMs ?? PWA_UPDATE_CHECK_MS;
  let cancelled = false;
  let hadController = Boolean(navigator.serviceWorker.controller);

  const maybeNotifyWaiting = () => {
    if (cancelled) {
      return;
    }
    if (shouldPromptForPwaUpdate(registration)) {
      onUpdateAvailable('waiting');
    }
  };

  registration.addEventListener('updatefound', () => {
    const newWorker = registration.installing;
    if (!newWorker) {
      return;
    }
    newWorker.addEventListener('statechange', () => {
      if (cancelled) {
        return;
      }
      if (newWorker.state === 'installed') {
        maybeNotifyWaiting();
      }
    });
  });

  const onControllerChange = () => {
    if (cancelled) {
      return;
    }
    if (hadController && navigator.serviceWorker.controller) {
      onUpdateAvailable('controller');
    }
    hadController = Boolean(navigator.serviceWorker.controller);
  };

  navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

  maybeNotifyWaiting();

  const pollForUpdate = () => {
    if (cancelled || document.visibilityState !== 'visible') {
      return;
    }
    registration
      .update()
      .then(() => {
        maybeNotifyWaiting();
      })
      .catch(() => {
        /* network / throttled update checks are non-fatal */
      });
  };

  const interval = setInterval(pollForUpdate, intervalMs);
  document.addEventListener('visibilitychange', pollForUpdate);
  window.addEventListener('pageshow', pollForUpdate);

  return () => {
    cancelled = true;
    clearInterval(interval);
    document.removeEventListener('visibilitychange', pollForUpdate);
    window.removeEventListener('pageshow', pollForUpdate);
    navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
  };
}
